import React, { useState, useEffect, useCallback } from 'react';
import { subAgentApplicationsAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiUserPlus, FiCheck, FiX, FiCopy } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const STATUS_COLORS = {
  PENDING: '#f5a623',
  APPROVED: '#43e97b',
  REJECTED: '#f5576c',
};

const ApproveModal = ({ application, onConfirm, onCancel, loading }) => {
  const [commissionSplit, setCommissionSplit] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 440 }}>
        <div className="modal-header"><h2 style={{ fontSize: 18 }}>Approve "{application.organization_name}"?</h2></div>
        <div className="modal-form">
          <p style={{ margin: 0, color: '#666', fontSize: 13, lineHeight: 1.5 }}>
            This creates a new Agency and an Agent-role account for {application.contact_name} ({application.contact_email}).
            A password will be generated — copy it before closing, it's shown only once.
          </p>
          <div className="form-group">
            <label>Commission split % (agency's share, optional)</label>
            <input type="number" min="0" max="100" step="0.5" value={commissionSplit} onChange={(e) => setCommissionSplit(e.target.value)} placeholder="e.g. 60" />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={() => onConfirm(commissionSplit || null)} disabled={loading} style={{ background: '#43e97b', borderColor: '#43e97b' }}>
            {loading ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ application, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 420 }}>
        <div className="modal-header"><h2 style={{ fontSize: 18 }}>Reject "{application.organization_name}"?</h2></div>
        <div className="modal-form">
          <div className="form-group">
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" rows={3} />
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={() => onConfirm(reason)} disabled={loading} style={{ background: '#dc2626', borderColor: '#dc2626' }}>
            {loading ? 'Rejecting…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CredentialsModal = ({ result, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-content" style={{ maxWidth: 460, borderColor: '#43e97b' }}>
      <div className="modal-header"><h2 style={{ fontSize: 18, color: '#16a34a' }}>Agency approved</h2></div>
      <div className="modal-form">
        <p style={{ margin: 0, color: '#666', fontSize: 13 }}>
          Share these credentials with {result.user.first_name} securely (not over an unencrypted channel). This password won't be shown again.
        </p>
        <div className="cv-subsection" style={{ margin: 0, fontFamily: 'monospace', fontSize: 13 }}>
          <div style={{ marginBottom: 8 }}>Username: <strong>{result.user.username}</strong></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Password: <strong>{result.generated_password}</strong>
            <button onClick={() => navigator.clipboard?.writeText(result.generated_password)} title="Copy" style={{ background: 'none', border: 'none', color: '#011ff3', cursor: 'pointer' }}>
              <FiCopy size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="modal-actions">
        <button onClick={onClose} className="btn-primary" style={{ flex: 1, background: '#43e97b', borderColor: '#43e97b' }}>Done</button>
      </div>
    </div>
  </div>
);

const SubAgentApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [credentialsResult, setCredentialsResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subAgentApplicationsAPI.list();
      setApplications(res.data.results || res.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (commissionSplit) => {
    setActionLoading(true);
    try {
      const res = await subAgentApplicationsAPI.approve(approveTarget.id, commissionSplit);
      setApproveTarget(null);
      setCredentialsResult(res.data);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to approve application.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason) => {
    setActionLoading(true);
    try {
      await subAgentApplicationsAPI.reject(rejectTarget.id, reason);
      setRejectTarget(null);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reject application.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiUserPlus size={22} color="#011ff3" />
            Sub-Agent Applications
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14 }}>
            Applications to become a B2B sub-agent or partner school — review before any account is created.
          </p>
        </div>
        <button className="btn-icon" onClick={load} title="Refresh">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} />
          <span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : applications.length === 0 ? (
          <div className="empty-state">No applications yet.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>{['Organization', 'Type', 'Contact', 'Country', 'Status', 'Actions'].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {applications.map((a) => {
                const color = STATUS_COLORS[a.status] || '#94a3b8';
                return (
                  <tr key={a.id}>
                    <td><strong>{a.organization_name}</strong></td>
                    <td>{{ PARTNER_SCHOOL: 'Partner School', HOSPITAL: 'Hospital' }[a.applicant_type] || 'Sub-Agent'}</td>
                    <td>{a.contact_name}<br /><span style={{ color: '#94a3b8', fontSize: 11 }}>{a.contact_email}</span></td>
                    <td>{a.country}</td>
                    <td><span className="status-pill" style={{ color }}>{a.status}</span></td>
                    <td>
                      {a.status === 'PENDING' && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => setApproveTarget(a)} className="cv-inline-btn" style={{ color: '#43e97b' }}>
                            <FiCheck size={13} /> Approve
                          </button>
                          <button onClick={() => setRejectTarget(a)} className="cv-inline-btn" style={{ color: '#f5576c' }}>
                            <FiX size={13} /> Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {approveTarget && <ApproveModal application={approveTarget} onConfirm={handleApprove} onCancel={() => setApproveTarget(null)} loading={actionLoading} />}
      {rejectTarget && <RejectModal application={rejectTarget} onConfirm={handleReject} onCancel={() => setRejectTarget(null)} loading={actionLoading} />}
      {credentialsResult && <CredentialsModal result={credentialsResult} onClose={() => setCredentialsResult(null)} />}
    </div>
  );
};

export default SubAgentApplications;
